import type { FC, ReactNode } from 'react'

import { Clock, MapPin, Navigation, Phone } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { LogoMark } from '@/components/generals/logo'
import { buttonVariants } from '@/components/ui/button'
import { contactInfo } from '@/constants/site'

export const ShowroomSection: FC = (): ReactNode => {
    return (
        <section id='showroom' aria-labelledby='showroom-title' className='py-20 lg:py-28'>
            <Container>
                <div className='relative isolate overflow-hidden rounded-[2rem] bg-foreground px-6 py-14 text-background sm:px-12 lg:px-16 lg:py-20'>
                    <LogoMark inverted className='pointer-events-none absolute -end-16 -top-16 -z-10 size-80 rotate-12 opacity-[0.06]' />

                    <div className='grid gap-12 lg:grid-cols-12 lg:items-center'>
                        <div className='lg:col-span-7'>
                            <p className='mb-4 flex items-center gap-3 text-sm font-medium text-accent'>
                                <span className='h-px w-8 bg-accent/60' aria-hidden='true' />
                                شوروم نارون
                            </p>

                            <h2 id='showroom-title' className='font-heading text-4xl font-semibold leading-[1.35] text-balance sm:text-5xl'>
                                از نزدیک ببینید، لمس کنید، بنشینید
                            </h2>

                            <p className='mt-5 max-w-xl text-base leading-8 text-background/75 sm:text-lg sm:leading-9'>
                                بهترین راه انتخاب مبل، نشستن روی آن است. در شوروم نارون همهٔ مدل‌ها، نمونه‌های پارچه و رنگ‌های چوب را از نزدیک ببینید و با مشاوران ما گفت‌وگو کنید.
                            </p>

                            <div className='mt-10 flex flex-wrap gap-3'>
                                <a
                                    href={contactInfo.mapUrl}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                    className={buttonVariants({ variant: 'light', size: 'lg' })}
                                >
                                    <Navigation aria-hidden='true' />
                                    مسیریابی تا شوروم
                                </a>

                                <a
                                    href={contactInfo.phoneHref}
                                    className={cn(buttonVariants({ variant: 'outline', size: 'lg' }), 'border-background/25 text-background hover:bg-background/10 hover:text-background')}
                                >
                                    <Phone aria-hidden='true' />
                                    <span dir='ltr'>
                                        {contactInfo.phone}
                                    </span>
                                </a>
                            </div>
                        </div>

                        <dl className='grid gap-4 lg:col-span-5'>
                            <div className='flex gap-4 rounded-2xl border border-background/10 bg-background/5 p-5'>
                                <MapPin className='mt-1 size-5 shrink-0 text-accent' aria-hidden='true' />
                                <div>
                                    <dt className='text-sm text-background/60'>
                                        آدرس شوروم
                                    </dt>
                                    <dd className='mt-1.5 text-sm leading-7 text-background'>
                                        {contactInfo.address}
                                    </dd>
                                </div>
                            </div>

                            <div className='flex gap-4 rounded-2xl border border-background/10 bg-background/5 p-5'>
                                <Clock className='mt-1 size-5 shrink-0 text-accent' aria-hidden='true' />
                                <div>
                                    <dt className='text-sm text-background/60'>
                                        ساعات کاری
                                    </dt>
                                    <dd className='mt-1.5 text-sm leading-7 text-background'>
                                        {contactInfo.hours}
                                        <span className='block text-background/60'>
                                            {contactInfo.closedDays}
                                        </span>
                                    </dd>
                                </div>
                            </div>
                        </dl>
                    </div>
                </div>
            </Container>
        </section>
    )
}
